import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { translations, LANGUAGES } from './translations';

const LanguageContext = createContext(null);

const STORAGE_KEY = 'gm_lang';

function detectInitialLang() {
  if (typeof window === 'undefined') return 'es';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && translations[stored]) return stored;
  const browser = (window.navigator.language || 'es').slice(0, 2).toLowerCase();
  return translations[browser] ? browser : 'es';
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState('es');

  useEffect(() => {
    const initial = detectInitialLang();
    setLangState(initial);
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
      const t = translations[lang]?.meta;
      if (t) {
        document.title = t.title;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', 'description');
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', t.description);
      }
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!translations[next]) return;
    setLangState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const t = useMemo(() => translations[lang] || translations.es, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, languages: LANGUAGES }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useI18n must be used within LanguageProvider');
  return ctx;
}
